/**
 * Hook for fetching asset data by ID with error handling
 *
 * Used for deep-linking asset preview modals where the asset is
 * identified by URL parameter rather than being passed as a prop.
 */

import { useQuery } from "@tanstack/react-query";
import { getAsset } from "@/services/api/sdk.gen";
import type { Asset } from "@/types/asset.types";
import { logger } from "@/services/logger";
import { generateUUID } from "@/utils/uuid";

/** UUID regex pattern for validation (supports v1-v7) */
const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Error types for asset preview deep-linking */
export type AssetPreviewError =
  | "not-found"
  | "no-access"
  | "invalid-id"
  | "wrong-project"
  | null;

export interface UseAssetPreviewOptions {
  /** Optional project ID to validate asset belongs to expected project */
  expectedProjectId?: string | null;
}

export interface UseAssetPreviewReturn {
  /** The fetched asset, or null if not loaded/error */
  asset: Asset | null;
  /** Whether the asset is currently being fetched */
  isLoading: boolean;
  /** Error state for display in UI */
  error: AssetPreviewError;
}

/**
 * Validates whether a string is a valid UUID format (any version)
 */
function isValidUuid(id: string): boolean {
  return UUID_REGEX.test(id);
}

/**
 * Hook to fetch an asset by ID for deep-linked previews
 *
 * @param assetId - The asset ID to fetch, or null to skip fetching
 * @param options - Optional configuration including project ID validation
 * @returns Object containing asset data, loading state, and error state
 *
 * @example
 * ```tsx
 * const { asset, isLoading, error } = useAssetPreview(assetIdFromUrl, {
 *   expectedProjectId: currentProjectId,
 * });
 *
 * if (isLoading) return <Spinner />;
 * if (error === 'not-found') return <NotFoundMessage />;
 * if (error === 'no-access') return <NoAccessMessage />;
 * if (error === 'wrong-project') return <WrongProjectMessage />;
 * if (asset) return <AssetPreview asset={asset} />;
 * ```
 */
export function useAssetPreview(
  assetId: string | null,
  options?: UseAssetPreviewOptions,
): UseAssetPreviewReturn {
  const { expectedProjectId } = options ?? {};
  // Validate UUID format before attempting fetch
  const isValidId = assetId !== null && isValidUuid(assetId);

  const { data, isLoading, error } = useQuery({
    queryKey: ["asset-preview", assetId],
    queryFn: async () => {
      if (!assetId) return null;

      const response = await getAsset({
        path: { assetId },
        throwOnError: true,
      });

      return (response?.data?.data as Asset) ?? null;
    },
    enabled: isValidId,
    staleTime: 30_000, // 30 seconds - asset data is relatively stable
    retry: false, // Don't retry on 404/401, those are definitive
  });

  // Map error states
  let errorState: AssetPreviewError = null;

  if (assetId !== null && !isValidId) {
    // Invalid UUID format
    errorState = "invalid-id";
  } else if (error) {
    // Map HTTP errors to user-friendly states
    const statusCode = (error as { status?: number })?.status;
    if (statusCode === 404) {
      errorState = "not-found";
    } else if (statusCode === 401 || statusCode === 403) {
      errorState = "no-access";
    } else {
      // Log unexpected errors for debugging, show as not-found to user
      logger.error("Unexpected error fetching asset for preview", error, {
        assetId,
        statusCode,
        correlationId: generateUUID(),
      });
      errorState = "not-found";
    }
  } else if (
    data &&
    expectedProjectId &&
    data.projectId !== expectedProjectId
  ) {
    // Asset belongs to a different project than expected
    errorState = "wrong-project";
  }

  return {
    asset: errorState ? null : (data ?? null),
    isLoading: isValidId && isLoading,
    error: errorState,
  };
}
